/**
 * 环境变量管理器，统一处理环境变量访问
 */
export class EnvManager {
  private static instance: EnvManager;
  private env: Record<string, string> = {};
  
  /**
   * 构造函数
   */
  private constructor() {
    // 导入所有环境变量
    if (typeof process !== 'undefined' && process.env) {
      Object.keys(process.env).forEach(key => {
        this.env[key] = process.env[key] || '';
      });
    }
    
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      Object.keys(import.meta.env).forEach(key => {
        this.env[key] = import.meta.env[key];
      });
    }

  }
  
  /**
   * 获取单例实例
   */
  static getInstance(): EnvManager {
    if (!EnvManager.instance) {
      EnvManager.instance = new EnvManager();
    }
    return EnvManager.instance;
  }
  
  /**
   * 获取环境变量值
   * @param key 环境变量名称
   * @param defaultValue 默认值
   * @returns 环境变量值或默认值
   */
  get(key: string, defaultValue: string = ''): string {
    // 先尝试从process.env获取
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key] || defaultValue;
    }
    
    // 再尝试从import.meta.env获取
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      return import.meta.env[key];
    }
    
    // 最后尝试从缓存中获取
    return this.env[key] || defaultValue;
  }
  
  /**
   * 获取数字类型环境变量
   * @param key 环境变量名称
   * @param defaultValue 默认值
   * @returns 环境变量值或默认值
   */
  getNumber(key: string, defaultValue: number = 0): number {
    const value = this.env[key];
    if (value === undefined || value === '') {
      return defaultValue;
    }
    
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }
  
  /**
   * 获取布尔类型环境变量
   * @param key 环境变量名称
   * @param defaultValue 默认值
   * @returns 环境变量值或默认值
   */
  getBoolean(key: string, defaultValue: boolean = false): boolean {
    const value = this.env[key];
    if (value === undefined || value === '') {
      return defaultValue;
    }
    
    return value.toLowerCase() === 'true';
  }
  
  /**
   * 获取JSON类型环境变量
   * @param key 环境变量名称
   * @param defaultValue 默认值
   * @returns 环境变量值或默认值
   */
  getJSON<T>(key: string, defaultValue: T): T {
    const value = this.env[key];
    if (value === undefined || value === '') {
      return defaultValue;
    }
    
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.warn(`环境变量 ${key} 不是有效的JSON: ${error}`);
      return defaultValue;
    }
  }
  
  /**
   * 检查环境变量是否存在
   * @param key 环境变量名称
   * @returns 是否存在
   */
  has(key: string): boolean {
    return this.env[key] !== undefined;
  }
  
  /**
   * 获取特定类型的所有环境变量
   * @param prefix 前缀
   * @returns 匹配的环境变量
   */
  getAllWithPrefix(prefix: string): Record<string, string> {
    const result: Record<string, string> = {};
    
    Object.keys(this.env).forEach(key => {
      if (key.startsWith(prefix)) {
        result[key] = this.env[key];
      }
    });
    
    return result;
  }
  
  /**
   * 获取所有API密钥环境变量
   * @returns API密钥环境变量
   */
  getAllApiKeys(): Record<string, string> {
    const result: Record<string, string> = {};
    
    Object.keys(this.env).forEach(key => {
      if (
        key.includes('API_KEY') || 
        key.includes('APIKEY') || 
        key.includes('TOKEN')
      ) {
        result[key] = '[REDACTED]'; // 安全考虑，不返回实际值
      }
    });
    
    return result;
  }
  
  /**
   * 检查必需的环境变量
   * @param keys 需要检查的环境变量列表
   * @returns 缺失的环境变量列表
   */
  checkRequired(keys: string[]): string[] {
    return keys.filter(key => !this.has(key) || this.get(key) === '');
  }
  
  /**
   * 获取环境变量
   */
  isDevelopment(): boolean {
    return this.get('NODE_ENV', 'development') === 'development';
  }
  
  /**
   * 是否为生产环境
   */
  isProduction(): boolean {
    return this.get('NODE_ENV', 'development') === 'production';
  }

  /**
   * 设置环境变量值
   * @param key 环境变量名称
   * @param value 环境变量值
   */
  set(key: string, value: string): void {
    this.env[key] = value;
  }
}
